'use client';

import { useEffect } from 'react';
import * as yup from 'yup';
import { useSearchParams } from 'next/navigation';
import styled from '@emotion/styled';
import ShopDineRelaxSearchWidget from '../search/ui/ShopDineRelaxSearchWidget';
import PoiList from './PoiList';
import { usePoiStore } from '../search/domain/hooks/usePoiStore';
import { PoiListScope } from '../search/domain/models/PoiListScope';
import { NoResults } from '../../../components/common/no-results/NoResults';
import { ContentWrap, Css, Responsive } from '../../../ui';
import { IPageEntry } from '../../../contentful/types/IPage';
import { getCurrentAirportCodeFromId } from '../../../components/common/navigation/utils/getCurrentAirportCode';
import { IPoiItem } from '../domain/models/IPoiItem';
import { AirportCodes } from '../../../types/airports';
import ShopDineRelaxFilters from '../search/ui/ShopDineRelaxFilters';
import { getPoiTypeKeywords } from '../domain/utils/getPoiTypeKeywords';
import { getAllTerminalLocationItems } from '../domain/utils/getAllTerminalLocationItems';
import { useFilterHandlers } from '../search/domain/hooks/useFilterHandlers';
import { usePaginationWithScroll } from '../search/domain/hooks/usePaginationWithScroll';
import { ErrorMessages } from '../../../utils/errorMessages';
import IAHIllustrationShopAndDine from '../../../assets/iah-illustration-shop-and-dine.svg';
import HOUIllustrationShopAndDine from '../../../assets/hou-illustration-shop-and-dine.svg';

const querySchema = yup.string().optional();
const scopeSchema = yup
	.mixed<PoiListScope>()
	.oneOf(Object.values(PoiListScope));

const StyledIllustrationWrapper = styled.div`
	width: ${Css.pxToRem(500)};
	height: ${Css.pxToRem(300)};

	@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
		width: ${Css.pxToRem(240)};
		height: ${Css.pxToRem(133)};
	}
`;

interface IShopDineRelaxPageProps {
	page: IPageEntry;
	poiData: IPoiItem[];
}

export default function ShopDineRelax({
	page,
	poiData,
}: IShopDineRelaxPageProps) {
	const searchParams = useSearchParams();
	const airportCode = getCurrentAirportCodeFromId(
		page.fields.parent?.fields.id,
	);

	const params = {
		query: searchParams.get('query'),
		scope: searchParams.get('scope'),
	};

	const queryParams = querySchema.isValidSync(params.query)
		? params.query
		: undefined;

	const scopeParams = scopeSchema.isValidSync(params.scope)
		? params.scope
		: undefined;

	const poiTypeKeywords = getPoiTypeKeywords(poiData);
	const poiTerminalLocations = getAllTerminalLocationItems(poiData);

	const {
		data,
		scope,
		setLocation,
		setOpeningHours,
		setSecurityZone,
		setType,
		setScope,
		setSearchQuery,
	} = usePoiStore(poiData);

	const { onSelectFilter } = useFilterHandlers(
		setLocation,
		setOpeningHours,
		setSecurityZone,
		setType,
	);

	const {
		ref,
		slicedPoiData,
		totalPages,
		currentPageIndex,
		setCurrentPageIndex,
	} = usePaginationWithScroll(data);

	useEffect(() => {
		setScope(scopeParams || PoiListScope.All);
		setSearchQuery(queryParams);
	}, [scopeParams, queryParams]);

	return (
		<ContentWrap ref={ref}>
			<ShopDineRelaxSearchWidget
				activeScope={scope}
				params={{ query: queryParams }}
				setCurrentPageIndex={setCurrentPageIndex}
				airportCode={airportCode}
			/>
			<ShopDineRelaxFilters
				poiTypeKeywords={poiTypeKeywords}
				locationItems={poiTerminalLocations}
				onSelectFilter={onSelectFilter}
			/>

			{slicedPoiData?.length === 0 ? (
				<NoResults
					title="No shops found"
					image={
						<StyledIllustrationWrapper>
							{airportCode === AirportCodes.HOU ? (
								<HOUIllustrationShopAndDine aria-label="Shop icon" />
							) : (
								<IAHIllustrationShopAndDine aria-label="Shop icon" />
							)}
						</StyledIllustrationWrapper>
					}
					message={ErrorMessages.ShopAndDineNoResults}
				/>
			) : (
				<PoiList
					slicedPoiData={slicedPoiData}
					totalPages={totalPages}
					currentPageIndex={currentPageIndex}
					setCurrentPageIndex={setCurrentPageIndex}
					airportCode={airportCode}
				/>
			)}
		</ContentWrap>
	);
}
